/**
 * Default DGF Rules Configuration
 *
 * These rules define the default thresholds and display settings for the DGF Report.
 * They are seeded into Firestore on first use and can be customized by admins.
 */
import { PROJECT_STATUS } from '@/utils/projectUtils';

export const DGF_ENTITY_TYPES = {
  DELIVERABLE: 'deliverable',
  PROJECT: 'project',
  EVENT: 'event'
};

export const DGF_TIME_UNITS = {
  BUSINESS_DAYS: 'businessDays',
  CALENDAR_DAYS: 'calendarDays',
  HOURS: 'hours'
};

export const DGF_OPERATORS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  IN: 'in',
  NOT_IN: 'not_in',
  GREATER_THAN: '>',
  LESS_THAN: '<',
  GREATER_THAN_OR_EQUAL: '>=',
  LESS_THAN_OR_EQUAL: '<='
};

export const DGF_CATEGORIES = {
  WORKING_ON_IT_OVERDUE: 'workingOnItOverdue',
  INTERNAL_REVIEW_OVERDUE: 'internalReviewOverdue',
  CLIENT_REVIEW_OVERDUE: 'clientReviewOverdue',
  STUCK_TOO_LONG: 'stuckTooLong',
  DUE_SOON: 'dueSoon',
  PAST_DUE: 'pastDue',
  PROJECTS_IN_REVIEW: 'projectsInReview',
  EVENTS_NEEDING_RECONCILIATION: 'eventsNeedingReconciliation',
  EVENTS_MISSING_PERSONNEL: 'eventsMissingPersonnel'
};

/**
 * Default DGF Rules
 * These match the current hardcoded behavior in dgfReportService.js
 */
export const DGF_DEFAULT_RULES = [
  // Deliverable Rules
  {
    name: 'Working On It Overdue',
    description: 'Deliverables stuck in "Working On It" status for too long',
    category: DGF_CATEGORIES.WORKING_ON_IT_OVERDUE,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.DELIVERABLE,
      field: 'status',
      operator: DGF_OPERATORS.EQUALS,
      value: 'working_on_it',
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.BUSINESS_DAYS,
        operator: DGF_OPERATORS.GREATER_THAN,
        value: 1
      },
      dateField: 'statusChangedAt',
      additionalFilters: []
    },
    display: {
      title: 'Working On It > 1 Business Day',
      icon: 'spinner',
      color: 'warning',
      durationLabel: 'Days in status',
      durationField: 'businessDaysInStatus',
      durationSuffix: 'days',
      warnThreshold: 2,
      dangerThreshold: 5
    },
    sortOrder: 3
  },
  {
    name: 'Internal Review Overdue',
    description: 'Deliverables in internal review stage for too long',
    category: DGF_CATEGORIES.INTERNAL_REVIEW_OVERDUE,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.DELIVERABLE,
      field: 'stage',
      operator: DGF_OPERATORS.EQUALS,
      value: 'internal_review',
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.BUSINESS_DAYS,
        operator: DGF_OPERATORS.GREATER_THAN,
        value: 4
      },
      dateField: 'stageChangedAt',
      additionalFilters: []
    },
    display: {
      title: 'Internal Review > 4 Business Days',
      icon: 'user-check',
      color: 'info',
      durationLabel: 'Days in stage',
      durationField: 'businessDaysInStage',
      durationSuffix: 'days',
      warnThreshold: 5,
      dangerThreshold: 10
    },
    sortOrder: 4
  },
  {
    name: 'Client Review Overdue',
    description: 'Deliverables awaiting client review for extended period (informational only)',
    category: DGF_CATEGORIES.CLIENT_REVIEW_OVERDUE,
    enabled: true,
    includeInBadgeCount: false, // Note: Not included in badge count
    condition: {
      entityType: DGF_ENTITY_TYPES.DELIVERABLE,
      field: 'stage',
      operator: DGF_OPERATORS.EQUALS,
      value: 'client_review',
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.BUSINESS_DAYS,
        operator: DGF_OPERATORS.GREATER_THAN,
        value: 10
      },
      dateField: 'stageChangedAt',
      additionalFilters: [
        {
          field: 'status',
          operator: DGF_OPERATORS.NOT_EQUALS,
          value: 'stuck'
        }
      ]
    },
    display: {
      title: 'Client Review > 10 Business Days',
      icon: 'users',
      color: 'primary',
      durationLabel: 'Days in stage',
      durationField: 'businessDaysInStage',
      durationSuffix: 'days',
      warnThreshold: 12,
      dangerThreshold: 20
    },
    actions: [
      {
        label: 'Stuck',
        icon: 'hand',
        buttonClass: 'is-danger',
        actionType: 'setStatus',
        field: 'status',
        value: 'stuck'
      }
    ],
    sortOrder: 5
  },
  {
    name: 'Stuck Too Long',
    description: 'Deliverables that have been stuck for an extended period',
    category: DGF_CATEGORIES.STUCK_TOO_LONG,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.DELIVERABLE,
      field: 'status',
      operator: DGF_OPERATORS.EQUALS,
      value: 'stuck',
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.CALENDAR_DAYS,
        operator: DGF_OPERATORS.GREATER_THAN,
        value: 30
      },
      dateField: 'statusChangedAt',
      additionalFilters: []
    },
    display: {
      title: 'Stuck > 30 Days',
      icon: 'exclamation-triangle',
      color: 'danger',
      durationLabel: 'Days stuck',
      durationField: 'daysStuck',
      durationSuffix: 'days',
      warnThreshold: 45,
      dangerThreshold: 60
    },
    sortOrder: 6
  },
  {
    name: 'Due Soon',
    description: 'Deliverables due within 24 hours',
    category: DGF_CATEGORIES.DUE_SOON,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.DELIVERABLE,
      field: 'dueDate',
      operator: DGF_OPERATORS.LESS_THAN_OR_EQUAL,
      value: null, // Computed at runtime
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.HOURS,
        operator: DGF_OPERATORS.LESS_THAN_OR_EQUAL,
        value: 24
      },
      dateField: 'dueDate',
      additionalFilters: []
    },
    display: {
      title: 'Due Within 24 Hours',
      icon: 'clock',
      color: 'warning',
      durationLabel: 'Hours until due',
      durationField: 'hoursUntilDue',
      durationSuffix: 'hrs',
      warnThreshold: 12,
      dangerThreshold: 6
    },
    sortOrder: 2
  },
  {
    name: 'Past Due',
    description: 'Deliverables that are past their due date',
    category: DGF_CATEGORIES.PAST_DUE,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.DELIVERABLE,
      field: 'dueDate',
      operator: DGF_OPERATORS.LESS_THAN,
      value: null, // Computed at runtime (now)
      timeThreshold: {
        enabled: false,
        unit: null,
        operator: null,
        value: null
      },
      dateField: 'dueDate',
      additionalFilters: []
    },
    display: {
      title: 'Past Due',
      icon: 'exclamation-circle',
      color: 'danger',
      durationLabel: 'Days overdue',
      durationField: 'daysOverdue',
      durationSuffix: 'days',
      warnThreshold: 1,
      dangerThreshold: 3
    },
    sortOrder: 1
  },

  // Project Rules
  {
    name: 'Projects In Review',
    description: 'Projects that have been in review status for too long',
    category: DGF_CATEGORIES.PROJECTS_IN_REVIEW,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.PROJECT,
      field: 'status',
      operator: DGF_OPERATORS.EQUALS,
      value: PROJECT_STATUS.IN_REVIEW,
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.BUSINESS_DAYS,
        operator: DGF_OPERATORS.GREATER_THAN,
        value: 3
      },
      dateField: 'statusChangedAt',
      additionalFilters: []
    },
    display: {
      title: 'Projects In Review > 3 Business Days',
      icon: 'hourglass-half',
      color: 'warning',
      durationLabel: 'Business days',
      durationField: 'daysInReview',
      durationSuffix: 'days',
      warnThreshold: 3,
      dangerThreshold: 7
    },
    actions: [
      {
        label: 'Active',
        icon: 'play',
        buttonClass: 'is-success',
        actionType: 'setStatus',
        field: 'status',
        value: PROJECT_STATUS.ACTIVE
      },
      {
        label: 'Complete',
        icon: 'check',
        buttonClass: 'is-info',
        actionType: 'setStatus',
        field: 'status',
        value: PROJECT_STATUS.COMPLETED
      }
    ],
    sortOrder: 1
  },

  // Event Rules
  {
    name: 'Events Needing Reconciliation',
    description: 'Past events that need post-event reconciliation',
    category: DGF_CATEGORIES.EVENTS_NEEDING_RECONCILIATION,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.EVENT,
      field: 'date',
      operator: DGF_OPERATORS.LESS_THAN,
      value: null, // Computed at runtime (today)
      timeThreshold: {
        enabled: true,
        unit: DGF_TIME_UNITS.BUSINESS_DAYS,
        operator: DGF_OPERATORS.GREATER_THAN_OR_EQUAL,
        value: 1
      },
      dateField: 'date',
      additionalFilters: [
        {
          field: 'postEventReconciliation',
          operator: DGF_OPERATORS.NOT_EQUALS,
          value: true
        },
        {
          field: 'type',
          operator: DGF_OPERATORS.NOT_IN,
          value: ['blocked', 'travel']
        }
      ]
    },
    display: {
      title: 'Events Needing Reconciliation',
      icon: 'calendar-check',
      color: 'info',
      durationLabel: 'Days since event',
      durationField: 'daysSinceEvent',
      durationSuffix: 'days',
      warnThreshold: 2,
      dangerThreshold: 5
    },
    actions: [
      {
        label: 'Reconciled',
        icon: 'check-circle',
        buttonClass: 'is-success',
        actionType: 'setField',
        field: 'postEventReconciliation',
        value: true
      }
    ],
    sortOrder: 1
  },
  {
    name: 'Events Missing Personnel',
    description: 'Upcoming events with no assigned team members',
    category: DGF_CATEGORIES.EVENTS_MISSING_PERSONNEL,
    enabled: true,
    includeInBadgeCount: true,
    condition: {
      entityType: DGF_ENTITY_TYPES.EVENT,
      field: 'date',
      operator: DGF_OPERATORS.GREATER_THAN_OR_EQUAL,
      value: null, // Computed at runtime (today)
      timeThreshold: {
        enabled: false,
        unit: null,
        operator: null,
        value: null
      },
      dateField: 'date',
      additionalFilters: [
        {
          field: 'assignedUserIds',
          operator: DGF_OPERATORS.EQUALS,
          value: [] // Empty array means no assignees
        },
        {
          field: 'type',
          operator: DGF_OPERATORS.NOT_IN,
          value: ['blocked', 'travel']
        }
      ]
    },
    display: {
      title: 'Events Missing Personnel',
      icon: 'user-slash',
      color: 'purple',
      durationLabel: 'Days until event',
      durationField: 'daysUntilEvent',
      durationSuffix: 'days',
      warnThreshold: 3,
      dangerThreshold: 1
    },
    sortOrder: 2
  }
];

/**
 * Available fields for each entity type (for the rule builder dropdown)
 */
export const DGF_ENTITY_FIELDS = {
  [DGF_ENTITY_TYPES.DELIVERABLE]: [
    { value: 'status', label: 'Status', type: 'select', options: ['not_started', 'ready', 'working_on_it', 'stuck', 'done'] },
    { value: 'stage', label: 'Stage', type: 'select', options: ['not_ready', 'organization', 'selects', 'color', 'post_queue', 'revision_queue', 'internal_review', 'client_review', 'approved', 'complete', 'other'] },
    { value: 'type', label: 'Type', type: 'select', options: ['standard', 'social_first', 'scripted', 'podcast', 'demo', 're_edit', 'hype', 'selects', 'other'] },
    { value: 'title', label: 'Title', type: 'text' },
    { value: 'description', label: 'Description', type: 'text' },
    { value: 'dueDate', label: 'Due Date', type: 'date' },
    { value: 'assignedUserId', label: 'Assigned User', type: 'user' },
    { value: 'primaryEditorId', label: 'Primary Editor', type: 'user' },
    { value: 'aspectRatio', label: 'Aspect Ratio', type: 'select', options: ['16:9', '9:16', '1:1', '4:5', 'custom'] },
    { value: 'resolution', label: 'Resolution', type: 'select', options: ['1080p', '4K'] },
    { value: 'runtime', label: 'Runtime', type: 'text' },
    { value: 'productionEventIds', label: 'Production Events', type: 'array' }
  ],
  [DGF_ENTITY_TYPES.PROJECT]: [
    { value: 'status', label: 'Status', type: 'select', options: [PROJECT_STATUS.DRAFT, PROJECT_STATUS.ACTIVE, PROJECT_STATUS.IN_REVIEW, 'On Hold', PROJECT_STATUS.COMPLETED] }
  ],
  [DGF_ENTITY_TYPES.EVENT]: [
    { value: 'date', label: 'Event Date', type: 'date' },
    { value: 'type', label: 'Event Type', type: 'select', options: ['shoot', 'meeting', 'blocked', 'travel'] },
    { value: 'postEventReconciliation', label: 'Reconciled', type: 'boolean' },
    { value: 'assignedUserIds', label: 'Assigned Users', type: 'array' }
  ]
};

/**
 * Icon options for the rule builder
 */
export const DGF_ICON_OPTIONS = [
  { value: 'spinner', label: 'Spinner' },
  { value: 'clock', label: 'Clock' },
  { value: 'exclamation-circle', label: 'Exclamation Circle' },
  { value: 'exclamation-triangle', label: 'Exclamation Triangle' },
  { value: 'user-check', label: 'User Check' },
  { value: 'users', label: 'Users' },
  { value: 'user-slash', label: 'User Slash' },
  { value: 'hourglass-half', label: 'Hourglass' },
  { value: 'calendar-check', label: 'Calendar Check' },
  { value: 'calendar-times', label: 'Calendar Times' },
  { value: 'check-circle', label: 'Check Circle' },
  { value: 'times-circle', label: 'Times Circle' }
];

/**
 * Color options for the rule builder
 */
export const DGF_COLOR_OPTIONS = [
  { value: 'danger', label: 'Danger (Red)' },
  { value: 'warning', label: 'Warning (Yellow)' },
  { value: 'info', label: 'Info (Blue)' },
  { value: 'success', label: 'Success (Green)' },
  { value: 'primary', label: 'Primary (Purple)' },
  { value: 'purple', label: 'Purple' }
];

/**
 * Action types for the rule builder
 */
const DGF_ACTION_TYPES = {
  SET_STATUS: 'setStatus',
  SET_STAGE: 'setStage',
  SET_FIELD: 'setField'
};

/**
 * Predefined action templates for quick selection
 */
export const DGF_ACTION_TEMPLATES = {
  [DGF_ENTITY_TYPES.DELIVERABLE]: [
    {
      label: 'Mark as Stuck',
      icon: 'hand',
      buttonClass: 'is-danger',
      actionType: DGF_ACTION_TYPES.SET_STATUS,
      field: 'status',
      value: 'stuck'
    },
    {
      label: 'Mark Done',
      icon: 'check',
      buttonClass: 'is-success',
      actionType: DGF_ACTION_TYPES.SET_STATUS,
      field: 'status',
      value: 'done'
    },
    {
      label: 'Start Working',
      icon: 'play',
      buttonClass: 'is-info',
      actionType: DGF_ACTION_TYPES.SET_STATUS,
      field: 'status',
      value: 'working_on_it'
    },
    {
      label: 'Send to Client Review',
      icon: 'paper-plane',
      buttonClass: 'is-primary',
      actionType: DGF_ACTION_TYPES.SET_STAGE,
      field: 'stage',
      value: 'client_review'
    },
    {
      label: 'Send to Internal Review',
      icon: 'user-check',
      buttonClass: 'is-info',
      actionType: DGF_ACTION_TYPES.SET_STAGE,
      field: 'stage',
      value: 'internal_review'
    }
  ],
  [DGF_ENTITY_TYPES.PROJECT]: [
    {
      label: 'Set Active',
      icon: 'play',
      buttonClass: 'is-success',
      actionType: DGF_ACTION_TYPES.SET_STATUS,
      field: 'status',
      value: PROJECT_STATUS.ACTIVE
    },
    {
      label: 'Set Complete',
      icon: 'check',
      buttonClass: 'is-info',
      actionType: DGF_ACTION_TYPES.SET_STATUS,
      field: 'status',
      value: PROJECT_STATUS.COMPLETED
    }
  ],
  [DGF_ENTITY_TYPES.EVENT]: [
    {
      label: 'Mark Reconciled',
      icon: 'check-circle',
      buttonClass: 'is-success',
      actionType: DGF_ACTION_TYPES.SET_FIELD,
      field: 'postEventReconciliation',
      value: true
    }
  ]
};

/**
 * Button class options for actions
 */
export const DGF_BUTTON_CLASS_OPTIONS = [
  { value: 'is-danger', label: 'Danger (Red)' },
  { value: 'is-warning', label: 'Warning (Yellow)' },
  { value: 'is-info', label: 'Info (Blue)' },
  { value: 'is-success', label: 'Success (Green)' },
  { value: 'is-primary', label: 'Primary (Purple)' }
];
